import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface BreadcrumbItem {
  name: string
  path?: string
}

interface PageSeoProps {
  title: string
  description: string
  image?: string
  type?: 'website' | 'article' | 'medicalbusiness'
  jsonLd?: Record<string, unknown>[]
  breadcrumb?: BreadcrumbItem[]
  className?: string
  children?: React.ReactNode
}

function truncateMetaDescription(description: string, maxLength = 125) {
  const trimmed = description.trim()

  if (trimmed.length <= maxLength) {
    return trimmed
  }

  return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`
}

function setMeta(name: string, content: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`
  let meta = document.querySelector(selector) as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    if (property) {
      meta.setAttribute('property', name)
    } else {
      meta.setAttribute('name', name)
    }
    document.head.appendChild(meta)
  }
  meta.content = content
}

function setLink(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', rel)
    document.head.appendChild(link)
  }
  link.href = href
}

export function PageSeo({
  title,
  description,
  image,
  type = 'website',
  jsonLd = [],
  breadcrumb,
  className,
  children,
}: PageSeoProps) {
  const { i18n } = useTranslation()
  const lang = i18n.language || 'it'
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : ''
  const siteUrl = import.meta.env.VITE_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')
  const ogImage = image
    ? image.startsWith('http')
      ? image
      : `${siteUrl}${image}`
    : `${siteUrl}/og-image.png`

  useEffect(() => {
    const metaDescription = truncateMetaDescription(description)

    document.documentElement.lang = lang === 'it' ? 'it-IT' : 'en'
    document.title = title

    setMeta('description', metaDescription)
    setLink('canonical', canonicalUrl)

    setMeta('og:title', title, true)
    setMeta('og:description', metaDescription, true)
    setMeta('og:image', ogImage, true)
    setMeta('og:url', canonicalUrl, true)
    setMeta('og:type', type === 'article' ? 'article' : 'website', true)
    setMeta('og:locale', lang === 'it' ? 'it_IT' : 'en_US', true)
    setMeta('og:site_name', 'AD Aesthetics', true)

    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', title)
    setMeta('twitter:description', metaDescription)
    setMeta('twitter:image', ogImage)
  }, [title, description, image, type, lang, canonicalUrl, ogImage])

  useEffect(() => {
    if (!breadcrumb || breadcrumb.length === 0) return

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.path
          ? `${canonicalUrl.split('/').slice(0, 3).join('/')}${item.path}`
          : undefined,
      })),
    }

    const id = 'breadcrumb-jsonld'
    let script = document.getElementById(id) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = id
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(structuredData)

    return () => {
      script?.remove()
    }
  }, [breadcrumb, canonicalUrl])

  useEffect(() => {
    if (jsonLd.length === 0) return

    const scripts: HTMLScriptElement[] = []
    jsonLd.forEach((data, index) => {
      const id = `page-jsonld-${index}`
      let script = document.getElementById(id) as HTMLScriptElement | null
      if (!script) {
        script = document.createElement('script')
        script.id = id
        script.type = 'application/ld+json'
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify(data)
      scripts.push(script)
    })

    return () => {
      scripts.forEach((s) => s.remove())
    }
  }, [jsonLd])

  return children ? <div className={cn(className)}>{children}</div> : null
}
